import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from "swiper";
import Imgix from 'react-imgix';
import breakpoints from '@/constants/breakpoints';

import 'swiper/swiper-bundle.min.css';
import useMedia from '@/hooks/useMedia';

function FeaturedCollections() {
  const baseImgURL = 'https://theinside.imgix.net/homepage/rebrand';

  const isPhone =
    typeof window !== `undefined` ? useMedia(`(max-width: ${breakpoints['standard-phone']}px)`) : null;
  const isTablet =
    typeof window !== `undefined` ? useMedia(`(max-width: 768px)`) : null;
  const is1240 =
    typeof window !== `undefined` ? useMedia(`(max-width: 1240px)`) : null;

  const cards = [
    {
      image: 'curtain-call.jpg',
      title: 'Curtain Call',
      description: 'Choose from prints, velvets, and solid linens to create the curtains you always dreamed of.',
      cta: {
        text: 'Shop Now',
        url: '/collections/bestselling-curtains'
      }
    },
    {
      image: 'small-seat.jpg',
      title: 'Small Seat, Big Impact',
      description: 'Add an extra seat—and some extra style—to your space with a made-to-order ottoman.',
      cta: {
        text: 'Shop Now',
        url: '/c/living-room-furniture/ottomans'
      }
    },
    {
      image: 'the-red-edit.jpg',
      title: 'The Red Edit',
      description: 'Our curated collection of must-have reds. Timeless pieces in a timeless shade.',
      cta: {
        text: 'Shop Now',
        url: '/collections/red-furniture'
      },
      customImgixParams: {
        'fit': 'crop',
        'crop': 'focalpoint',
        'fp-x': 0.5,
        'fp-y': 0.6,
        'fp-z': 1.1
      }
    }
  ];

  const carouselBreakpoints = {
    400: {
      slidesPerView: 1.5
    },
    500: {
      slidesPerView: 1.75
    },
    585: {
      slidesPerView: 2.25
    },
    925: {
      slidesPerView: 3
    },
  }

  return (
    <Container>
      <div style={{ position: 'relative' }}>
        <Swiper
          spaceBetween={0}
          slidesPerView={1.2}
          className="featured-slider"
          breakpoints={carouselBreakpoints}
          modules={[Navigation]}
        >
          { cards.map( ({ image, title, description, cta: { text: ctaText, url: ctaUrl }, customImgixParams}) => (
            <SwiperSlide key={image}>
              <div style={{ position: 'relative' }}>
                <Overlay />
                <Imgix
                  sizes={`(min-width: ${breakpoints['xlarge-desktop'] + 1}px) 33vw, 540px`}
                  imgixParams={{
                    auto: 'compress,format',
                    ...customImgixParams
                  }}
                  src={`${baseImgURL}/${image}`}
                  height={isPhone ? 430 : isTablet ? 440 : is1240 ? 535 : 700}
                  htmlAttributes={{
                    alt: title,
                    loading: 'lazy'
                  }}
                />
                <TopText>
                  <Title>{title}</Title>
                  <Description>{description}</Description>
                  <Cta href={ctaUrl}>{ctaText}</Cta>
                </TopText>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>


    </Container>
  )
}

const Container = styled.div`
  margin: 0 auto;
  max-width: 1620px;
  img {
    width: 100%;
    object-fit: cover;
  }
`;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.5) 100%);
  background-blend-mode: normal;
  z-index: 2;
  pointer-events: none;
`

const TopText = styled.div`
  position: absolute;
  bottom: 29px;
  color: #fff;
  z-index: 3;
  margin-left: 17.7px;
  padding-right: 10px;

  @media (min-width: ${breakpoints['tablet']}px) {
    margin-left: 21.3px;
    bottom: 34px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    margin-left: 14.5px;
    bottom: 41px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    margin-left: 15px;
    bottom: 61px;
  }
`

const Title = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  letter-spacing: ${({ theme }) => theme.fonts.secondary.letterSpacing};
  font-weight: 300;
  font-size: 26px;
  line-height: 26px;
  letter-spacing: -0.52px;
  margin-bottom: 13px;
  color: #fff;
  z-index: 3;

  @media (min-width: ${breakpoints['tablet']}px) {
    font-size: 28px;
    line-height: 26px;
    letter-spacing: -0.56px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 38px;
    line-height: 1.32;
    letter-spacing: -0.76px;
    margin-bottom: 7px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 46px;
    line-height: 1.09;
    letter-spacing: -1.38px;
    margin-bottom: 16px;
  }
`

const Description = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  font-size: 14px;
  line-height: 1.29;
  margin-bottom: 13px;
  color: #fff;
  z-index: 3;
  min-height: 36.11px;

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 14px;
    max-width: unset;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 18px;
    line-height: 1.45;
    margin-bottom: 22px;
    min-height: 48px;
  }
`

const Cta = styled.a`
  display: inline-block;
  color: #fff;
  border-radius: 35px;
  border: solid 1px #fff;
  padding: 13.8px 24.1px 12.5px 23.6px;

  &:hover {
    color: #fff;
    text-decoration: none;
  }
`

export default FeaturedCollections;
